import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import Breadcrumbs from 'components/shared/Breadcrumbs';
import Button from 'components/shared/Button';
import Search from 'components/shared/Search';
import { useDebounce } from 'hooks/useDebounce';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import usePagination from 'hooks/usePagination';
import { BulkPayerType, GroupPayerNetworkStatus, NetworkStatus } from 'models/GroupPayerNetworkStatus';
import {
  groupPayerNetworkStatusQueryKeys,
  indexGroupPayerNetworkStatuses,
  NetworkStatusFilter,
  updateGroupPayerNetworkStatuses,
} from 'services/api/groupPayerNetworkStatuses';
import { validationErrorMessage } from 'services/api/validationErrors';
import { serializeSort } from 'stores/helpers';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';
import ArrowLeft from 'svg/ArrowLeft';
import PlusIcon from 'svg/PlusIcon';

import AddPayerNetworksModal from './AddPayerNetworksModal';
import BulkEditPanel from './BulkEditPanel';
import { countOf } from './facilityNetworkStatusColumns';
import { Title } from './FacilityNetworkStatusPage';
import { useNetworkStatusScope } from './networkStatusScope';
import NetworkStatusTable, { nameParam, NetworkStatusKind, removedElsewhere } from './NetworkStatusTable';
import RemoveNetworkStatusModal from './RemoveNetworkStatusModal';

const COPY = {
  payer: {
    allLabel: 'All Payers',
    allPath: (basePath: string) => `${basePath}?view=payers`,
    search: 'Search Facility',
    addLabel: 'Add Facilities',
    noun: ['Facility', 'Facilities'] as [string, string],
    intro:
      'Indicate the status for the facilities listed below by their type (Commercial, Managed Medicare, Managed Medicaid, Other). If a facility is not listed below, click ‘Add Facilities’ to search for additional facilities to add.',
  },
  facility: {
    allLabel: 'All Facilities',
    allPath: (basePath: string) => basePath,
    search: 'Search Payer',
    addLabel: 'Add Payers',
    noun: ['Payer', 'Payers'] as [string, string],
    intro:
      'Indicate the status for the payers listed below by their type (Commercial, Managed Medicare, Managed Medicaid, Other). If a payer is not listed below, click ‘Add Payers’ to search for additional payers to add.',
  },
};

export default function NetworkStatusPage({ kind }: { kind: NetworkStatusKind }) {
  const { stediPayerId = '', groupId = '' } = useParams();
  const subjectId = kind === 'payer' ? stediPayerId : groupId;

  // React Router reuses this element from one subject to the next; the key starts each subject
  // afresh, so one's selection can't be applied from another's page.
  return <SubjectNetworkStatus key={`${kind}:${subjectId}`} kind={kind} subjectId={subjectId} />;
}

function SubjectNetworkStatus({ kind, subjectId }: { kind: NetworkStatusKind; subjectId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { clientId, basePath, canEdit } = useNetworkStatusScope();
  const copy = COPY[kind];
  const allPath = copy.allPath(basePath);

  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>([{ id: nameParam(kind), desc: false }]);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);
  // Ids, not rows: a selection spans pages and searches, so most selected rows aren't loaded.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [removing, setRemoving] = useState<GroupPayerNetworkStatus | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const filter: NetworkStatusFilter = kind === 'payer' ? { stediPayerId: subjectId } : { groupId: subjectId };
  const queryParams = {
    clientId,
    ...filter,
    ...pagination.paging,
    sortBy: serializeSort(sort),
    [nameParam(kind)]: search,
  };
  // Keep the last page while a search, sort or page change loads, so the title doesn't blank out.
  const { data, isLoading, isPlaceholderData } = useQuery({
    queryKey: groupPayerNetworkStatusQueryKeys.rows(queryParams),
    queryFn: ({ signal }) => indexGroupPayerNetworkStatuses(queryParams, signal),
    placeholderData: keepPreviousData,
  });

  // Removing the only row on the last page would otherwise leave an empty page. No pages at all still has page 1.
  const totalPages = data?.meta.totalPages;
  useEffect(() => {
    if (totalPages === undefined) return;
    const lastPage = Math.max(totalPages, 1);
    if (pagination.paging.page > lastPage) pagination.setPageIndex(lastPage - 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPages]);

  // The landing link passes the name along, so the title shows before the first page loads.
  const name = (kind === 'payer' ? data?.stediPayer : data?.group)?.name ?? location.state?.name ?? '';

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();
  // A 404 means a selected row was removed elsewhere; retrying the same ids can never succeed.
  const bulk = useMutateWithToast({
    mutationFn: (body: Omit<Parameters<typeof updateGroupPayerNetworkStatuses>[0], 'clientId'>) =>
      updateGroupPayerNetworkStatuses({ clientId, ...body }),
    errorToast: (error) =>
      removedElsewhere(error)
        ? `Some selected ${copy.noun[1].toLowerCase()} were removed by someone else. The list has been refreshed.`
        : validationErrorMessage(error),
    onError: (error) => {
      if (!removedElsewhere(error)) return;
      invalidateQueries({ groupPayerNetworkStatuses: true });
      setSelected(new Set());
    },
  });

  const handleApply = (payerType: BulkPayerType, status: NetworkStatus) => {
    const ids = [...selected];
    bulk.mutate(
      { ids, payerType, status },
      {
        onSuccess: () => {
          invalidateQueries({ groupPayerNetworkStatuses: true });
          addToast({
            text: `Changes applied to ${countOf(ids.length, copy.noun[0].toLowerCase(), copy.noun[1].toLowerCase())}`,
          });
          setSelected(new Set());
        },
      }
    );
  };

  const dropFromSelection = (id: string) =>
    setSelected((current) => new Set([...current].filter((selectedId) => selectedId !== id)));

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <BackButton type='button' aria-label='Back to Facility Network Status' onClick={() => navigate(allPath)}>
            <ArrowLeft />
          </BackButton>
          <Title>{name && `${name} Network Status`}</Title>
          <SearchContainer>
            <Search value={searchInput} onChange={setSearchInput} placeholder={copy.search} />
          </SearchContainer>
          {canEdit && (
            <AddButton variant='outline-primary' disabled={!name} onClick={() => setAdding(true)}>
              <PlusIcon width={16} height={16} />
              {copy.addLabel}
            </AddButton>
          )}
        </HeaderRow>
      </Page.Header>

      <Page.Content>
        <Crumbs>
          <Breadcrumbs
            breadcrumbs={[
              { id: 'all', label: copy.allLabel, meta: allPath },
              { id: 'current', label: name, meta: '' },
            ]}
            onClick={(crumb) => navigate(crumb.meta)}
          />
        </Crumbs>
        <Intro>{copy.intro}</Intro>

        {canEdit && selected.size > 0 && (
          <BulkEditPanel
            count={selected.size}
            noun={copy.noun}
            applying={bulk.isPending}
            onApply={handleApply}
            onClear={() => setSelected(new Set())}
          />
        )}

        <NetworkStatusTable
          kind={kind}
          rows={data?.data ?? []}
          loading={isLoading || isPlaceholderData}
          pageCount={totalPages ?? 1}
          pagination={pagination}
          sorting={{ sortBy: sort, setSortBy: setSort }}
          selected={selected}
          onSelectedChange={setSelected}
          onRemove={setRemoving}
        />
      </Page.Content>

      {removing && (
        <RemoveNetworkStatusModal
          kind={kind}
          row={removing}
          onClose={() => setRemoving(null)}
          onRemoved={dropFromSelection}
        />
      )}

      {adding && (
        <AddPayerNetworksModal
          variant={
            kind === 'payer'
              ? { kind: 'payerGroups', stediPayer: { id: subjectId, name } }
              : { kind: 'groupPayers', group: { id: subjectId, name } }
          }
          onClose={() => setAdding(false)}
        />
      )}
    </Page>
  );
}

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
`;

const AddButton = styled(Button)`
  margin-left: auto;
`;

const BackButton = styled.button`
  display: flex;
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

// The shared Breadcrumbs are sized for Insights (10px, 4px around the separator); Figma has these at 14px with 8px.
const Crumbs = styled.div`
  div {
    gap: 8px;
  }

  a,
  span {
    font-size: 14px;
    line-height: 22px;
  }
`;

// The shared Search is 38px tall; Figma has this one at 36px.
const SearchContainer = styled.div`
  width: 200px;
  flex-shrink: 0;

  & > div {
    height: 36px;
  }
`;

const Intro = styled(BodySmall)`
  display: block;
  margin: 24px 0 18px;
  font-size: 14px;
  line-height: 22px;
  color: ${({ theme }) => theme.colors.black100};
`;
